import React, {useEffect} from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import OdxIcon from '../Odx-Icon'
import {OdxInputError} from './Interfaces'
import OdxInputField from "./Odx-InputField";

interface OdxDatepickerProps {
    placeholder?: string
    isRequired: boolean
    index: string
    label: string
    error?: OdxInputError
    odxIcon?: React.ReactElement<typeof OdxIcon>
    justifyIcon?: 'start' | 'end'
    selectedDate?: Date
    onChange?: (date: Date | null) => void,
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void
    className?: string
    isDisabled?: boolean
    hint?: string
    value?: string
    setValue?: (value: string) => void
}

/** Usage
 * <OdxDatepicker
 *                 label={'Choose a date'}
 *                 selectedDate={selectedDate}
 *                 justifyIcon={'end'}
 *                 onChange={(date) => setSelectedDate(date)}
 *                 odxIcon={<OdxIcon iconSet={odxIconSets.core} size={'md'} name={'calendar'} />}
 *                 index={'datepicker'}
 *                 error={{ text: 'This field is required', isActive: !selectedDate }}
 *                 isRequired={true}
 *               />
 */

/**
 * Datepicker component. Is used to display a date picker field.
 * @param placeholder placeholder text to display in the input field when no value is entered
 * @param index unique index for the input field is used for filling the id and references
 * @param label label to display above the input field
 * @param isRequired if true, the input field will be marked as required (optional)
 * @param selectedDate selected date value of the date picker
 * @param onChange function to call when the value of the date picker changes
 * @param onBlur
 * @param error error to display below the input field (optional)
 * @param justifyIcon position of the icon in the input field (optional)
 * @param odxIcon icon to display in the input field (optional)
 * @param className
 * @param isDisabled
 * @param hint
 */

const OdxDatepicker = ({
                           placeholder = 'DD.MM.YYYY',
                           index,
                           label,
                           isRequired = false,
                           odxIcon,
                           selectedDate,
                           onChange,
                           onBlur,
                           error,
                           justifyIcon = 'end',
                           className,
                           isDisabled = false,
                           hint = '',
                           value = "",
                           setValue
                       }: OdxDatepickerProps) => {

    const [errorState, setErrorState] = React.useState<OdxInputError>({isActive: false, text: ''})
    useEffect(() => {
        const isValidDate = (dateString: string) => {
            // Check for both formats: 'dd.mm.yyyy' and 'yyyy-mm-ddTHH:MM:SS+ZZ:ZZ'
            const regexOldFormat = /^\d{2}\.\d{2}\.\d{4}$/;
            const regexNewFormat = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\+\d{2}:\d{2}$/;

            if (!regexOldFormat.test(dateString) && !regexNewFormat.test(dateString)) {
                return false;
            }

            let date;
            if (regexOldFormat.test(dateString)) {
                const [day, month, year] = dateString.split('.').map(Number);
                date = new Date(year, month - 1, day);
            } else {
                // Handle new format
                date = new Date(dateString);
                if (isNaN(date.getTime())) {
                    return false; // Invalid date
                }
            }

            const currentDate = new Date();
            currentDate.setHours(0, 0, 0, 0);

            return date >= currentDate;
        };

        if (value === '') {
            setErrorState({isActive: false, text: ''});
        } else if (!isValidDate(value)) {
            setErrorState({isActive: true, text: 'Please enter a valid date'});
        } else {
            setErrorState({isActive: false, text: ''});
        }
    }, [value]);



    return (
        <OdxInputField
            isRequired={isRequired}
            id={index}
            label={label}
            placeholder={placeholder}
            value={value}
            error={errorState}
            onChange={(e) => setValue(e.target.value)}
        />
    )
}

export default OdxDatepicker;